/**
 * useDogs Hook - Multi-dog management with localStorage fallback
 *
 * Key features:
 * - All /api/* fetches use fetchJson (credentials: include) to avoid domain/cookie mismatch 401s
 * - GET uses server data as source of truth when available, localStorage only when DB is not configured
 * - 401 triggers onUnauthorized (sign-in UX) without crashing React
 * - AbortError is caught and ignored (no user-visible error)
 */

import { useState, useEffect, useCallback } from 'react'
import { type Dog, type DogFormData } from '@/types/dog'
import { fetchJson } from '@/lib/http/fetchJson'
import { trackEvent } from '@/lib/analytics'
import { interpretDogsListApiPayload } from '@/lib/dogs/dogListQuery'

interface UseDogsOptions {
  onUnauthorized?: () => void // Callback when 401 (e.g. open Google sign-in prompt)
}

/**
 * Helper to load dogs from localStorage for a given userId
 */
function loadDogsFromLocal(userId: string): Dog[] {
  if (typeof window === 'undefined') return []

  try {
    const key = `wagamour_dogs_${userId}`
    const raw = window.localStorage.getItem(key)
    if (!raw) return []

    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    console.error('[useDogs] Failed to load dogs from localStorage', err)
    return []
  }
}

export function useDogs(userId: string | null, options?: UseDogsOptions) {
  const { onUnauthorized } = options || {}
  const [dogs, setDogs] = useState<Dog[]>([])
  const [loading, setLoading] = useState(false)
  /** True only after a dogs request completes for the current userId (not the initial empty array). */
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)

  // Load dogs
  const loadDogs = useCallback(async () => {
    if (!userId) {
      setDogs([])
      setLoading(false)
      setLoaded(false)
      setError(null)
      return
    }

    setLoading(true)
    setLoaded(false)
    setError(null)

    try {
      console.log('[useDogs] Loading dogs from database for userId:', userId)
      const result = await fetchJson<{
        success?: boolean
        dogs?: Dog[]
        code?: string
        message?: string
      }>(`/api/dogs?userId=${encodeURIComponent(userId)}`)
      const data = result.ok
        ? result.data
        : {
            success: false,
            code: (result as { data?: { code?: string } }).data?.code,
            message: (result as { data?: { message?: string } }).data?.message,
          }
      const interpretation = interpretDogsListApiPayload(data)

      // Soft-fail payloads often still send success:true + dogs:[]. Never treat those
      // as authoritative zero dogs (Play add-dog false empty).
      if (interpretation.kind === 'db_unavailable') {
        console.warn(
          '[useDogs] Database not available - not treating as empty dog list:',
          interpretation.code
        )
        const localDogs = userId ? loadDogsFromLocal(userId) : []
        setDogs(localDogs)
        setError(
          interpretation.message ||
            'Could not load dogs from the server. Showing saved dogs if available.'
        )
        return
      }

      if (
        interpretation.kind === 'dogs' ||
        interpretation.kind === 'authoritative_empty'
      ) {
        const serverDogs =
          interpretation.kind === 'dogs' ? (interpretation.dogs as Dog[]) : []
        const localDogs = userId ? loadDogsFromLocal(userId) : []

        console.log('[useDogs] Database returned:', serverDogs.length, 'dogs')
        console.log('[useDogs] localStorage has:', localDogs.length, 'dogs')

        const uniqueServerDogs = serverDogs.reduce((acc: Dog[], dog: Dog) => {
          if (!acc.find((d) => d.id === dog.id)) {
            acc.push(dog)
          }
          return acc
        }, [])

        if (uniqueServerDogs.length > 0) {
          console.log(
            '[useDogs] ✅ Using database dogs as source of truth:',
            uniqueServerDogs.length,
            'dogs'
          )
          setDogs(uniqueServerDogs)
          setError(null)

          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(
                `wagamour_dogs_${userId}`,
                JSON.stringify(uniqueServerDogs)
              )
              console.log('[useDogs] Saved database dogs to localStorage')
            } catch (e) {
              console.warn('[useDogs] Failed to save to localStorage:', e)
            }
          }
        } else if (localDogs.length > 0) {
          // Authoritative empty from the session-scoped server list. Do NOT resurrect
          // localStorage dogs (stale account / temp_* / wrong userId key) — those caused
          // Play to call /api/play/* with a dogId the session does not own → 403.
          console.log(
            '[useDogs] Server authoritative empty; ignoring',
            localDogs.length,
            'localStorage dog(s)'
          )
          setDogs([])
          setError(null)
        } else {
          // Authoritative empty on server and local — truly no dogs yet
          console.log('[useDogs] No dogs on server or localStorage')
          setDogs([])
          setError(null)
        }
        return
      }

      {
        // Real API error - show to user
        const dataErr = data as { message?: string; code?: string }
        const errorMsg = dataErr?.message || 'Failed to load dogs'
        setError(errorMsg)
        console.error('[useDogs] Load error:', {
          status: result.status,
          code: dataErr?.code,
          message: errorMsg,
          fullResponse: data,
        })

        if (typeof window !== 'undefined') {
          try {
            const cached = localStorage.getItem(`wagamour_dogs_${userId}`)
            if (cached) {
              const cachedDogs = JSON.parse(cached)
              if (Array.isArray(cachedDogs)) {
                const uniqueCachedDogs = cachedDogs.reduce(
                  (acc: Dog[], dog: Dog) => {
                    if (!acc.find((d) => d.id === dog.id)) {
                      acc.push(dog)
                    }
                    return acc
                  },
                  []
                )
                setDogs(uniqueCachedDogs)
                console.log(
                  '[useDogs] Loaded from localStorage after error:',
                  uniqueCachedDogs.length,
                  'dogs'
                )
              }
            }
          } catch (e) {
            console.warn(
              '[useDogs] Failed to load from localStorage after error:',
              e
            )
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setLoading(false)
        return
      }
      // Network error or fetch failure - try localStorage fallback
      console.error('[useDogs] Network/fetch error:', err)
      const errorMsg =
        err.message || 'Network error - please check your connection'
      setError(errorMsg)

      // Try localStorage as fallback
      if (typeof window !== 'undefined') {
        try {
          const cached = localStorage.getItem(`wagamour_dogs_${userId}`)
          if (cached) {
            const cachedDogs = JSON.parse(cached)
            if (Array.isArray(cachedDogs)) {
              const uniqueCachedDogs = cachedDogs.reduce(
                (acc: Dog[], dog: Dog) => {
                  if (!acc.find((d) => d.id === dog.id)) {
                    acc.push(dog)
                  }
                  return acc
                },
                []
              )
              setDogs(uniqueCachedDogs)
              console.log(
                '[useDogs] Loaded from localStorage after network error:',
                uniqueCachedDogs.length,
                'dogs'
              )
            }
          }
        } catch (e) {
          console.warn(
            '[useDogs] Failed to load from localStorage after network error:',
            e
          )
          setDogs([])
        }
      } else {
        setDogs([])
      }
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [userId])

  // Helper function to persist dog to localStorage with enhanced error handling
  const persistDogToLocal = useCallback((userId: string, dog: Dog) => {
    if (typeof window === 'undefined') {
      console.warn(
        '[useDogs] Cannot save to localStorage - window is undefined'
      )
      return
    }

    try {
      const key = `wagamour_dogs_${userId}`

      // Check if localStorage is available
      if (!window.localStorage) {
        console.error(
          '[useDogs] localStorage is not available in this environment'
        )
        return
      }

      const existingRaw = localStorage.getItem(key)
      const existing: Dog[] = existingRaw ? JSON.parse(existingRaw) : []

      const idx = existing.findIndex((d) => d.id === dog.id)
      let updatedDogs: Dog[]

      if (idx >= 0) {
        updatedDogs = [...existing]
        updatedDogs[idx] = dog
      } else {
        updatedDogs = [...existing, dog]
      }

      // Deduplicate by id
      const uniqueDogs = updatedDogs.reduce((acc: Dog[], d: Dog) => {
        if (!acc.find((existing) => existing.id === d.id)) {
          acc.push(d)
        }
        return acc
      }, [])

      // Serialize and save with quota error handling
      const serialized = JSON.stringify(uniqueDogs)

      try {
        localStorage.setItem(key, serialized)
        window.dispatchEvent(new CustomEvent('dogsUpdated'))
        console.log('[useDogs] ✓ Dog saved to localStorage successfully:', {
          dogId: dog.id,
          dogName: dog.name,
          totalDogs: uniqueDogs.length,
          storageKey: key,
          dataSize: `${(serialized.length / 1024).toFixed(2)} KB`,
        })
      } catch (storageError: any) {
        // Handle quota exceeded or other storage errors
        if (
          storageError.name === 'QuotaExceededError' ||
          storageError.code === 22
        ) {
          console.error(
            '[useDogs] ❌ localStorage quota exceeded - cannot save dog profile',
            {
              dogId: dog.id,
              dataSize: `${(serialized.length / 1024).toFixed(2)} KB`,
              error: storageError.message,
            }
          )
          // Try to clear old data or notify user
          alert(
            'Storage space is full. Please clear some data or use a different device.'
          )
        } else if (storageError.name === 'SecurityError') {
          console.error(
            '[useDogs] ❌ localStorage access denied (SecurityError) - PWA may have storage restrictions',
            {
              dogId: dog.id,
              error: storageError.message,
            }
          )
        } else {
          console.error('[useDogs] ❌ Failed to save to localStorage:', {
            dogId: dog.id,
            error: storageError.name || storageError.message || storageError,
          })
        }
        // Still update state even if localStorage fails (for current session)
        setDogs(uniqueDogs)
        return
      }

      // Update state after successful save
      setDogs(uniqueDogs)
    } catch (e: any) {
      console.error('[useDogs] ❌ Failed to persist dog to localStorage:', {
        error: e.name || e.message || e,
        dogId: dog.id,
        userId: userId,
      })
      // Still update state for current session
      setDogs((prev) => {
        const idx = prev.findIndex((d) => d.id === dog.id)
        if (idx >= 0) {
          return prev.map((d, i) => (i === idx ? dog : d))
        }
        return [...prev, dog]
      })
    }
  }, [])

  // Save dog (create or update)
  const saveDog = useCallback(
    async (
      dogData: DogFormData,
      dogId?: string
    ): Promise<{
      success: boolean
      error?: string
      dog?: Dog
      source?: string
      requiresUpgrade?: boolean
      code?: string
    }> => {
      if (!userId) {
        const errorMsg = 'Please sign in to save your dog profile.'
        setError(errorMsg)
        // Trigger auth prompt if callback provided
        if (onUnauthorized) {
          onUnauthorized()
        }
        return { success: false, error: errorMsg }
      }

      setSaving(true)
      setError(null)

      // Payload shape sent to /api/dogs POST:
      // {
      //   userId: string (required)
      //   name: string (required)
      //   breed: string (required)
      //   size: 'small' | 'medium' | 'large' (required, defaults to 'medium' if missing)
      //   ageBracket: 'puppy' | 'adult' | 'senior' (required, defaults to 'adult' if missing)
      //   photoUrl?: string
      //   energyLevel?: 'low' | 'medium' | 'high'
      //   sociabilityDogs?: 'shy' | 'neutral' | 'friendly' | 'selective' | 'reactive'
      //   sociabilityPeople?: 'shy' | 'neutral' | 'friendly' | 'guarded'
      //   playStyles: string[] (required array, defaults to [] if missing)
      //   leashBehavior?: 'easy' | 'pulls' | 'reactive'
      //   noiseSensitivity?: 'low' | 'medium' | 'high'
      //   crowdTolerance?: 'low' | 'medium' | 'high'
      //   weatherTolerance: string[] (required array, defaults to [] if missing)
      //   surfaceSensitivity?: 'normal' | 'sensitive_paws'
      //   carComfort?: 'loves_car' | 'okay_in_car' | 'anxious_in_car' | 'motion_sick'
      //   hasJointIssues: boolean (defaults to false)
      //   hasBreathingIssues: boolean (defaults to false)
      //   hasAllergies: boolean (defaults to false)
      //   allergyTags: string[] (required array, defaults to [] if missing)
      //   traitsJson?: object (optional, for extra fields like gender, age, weight, etc.)
      // }
      const payload = { ...dogData, userId }
      console.log('[useDogs] Saving dog profile payload:', {
        dogId,
        hasName: !!dogData.name,
        hasBreed: !!dogData.breed,
        size: dogData.size,
        ageBracket: dogData.ageBracket,
        hasUserId: !!userId,
        payloadKeys: Object.keys(payload),
      })

      try {
        const url = dogId ? `/api/dogs/${dogId}` : '/api/dogs'
        const method = dogId ? 'PUT' : 'POST'
        const result = await fetchJson<{
          success?: boolean
          dog?: Dog
          code?: string
          message?: string
          requiresUpgrade?: boolean
          currentUsage?: number
          limit?: number
        }>(url, { method, body: JSON.stringify(payload) })

        console.log('[useDogs] Save response status:', result.status)

        const responseData = result.ok
          ? result.data
          : (result as { data?: any }).data

        // Check if server save failed - fallback to localStorage for ALL failures except 401 and 403 (dog limit)
        if (!result.ok || !responseData || responseData.success === false) {
          // Handle 401 Unauthorized - trigger sign-in UX, do not fallback
          if (
            result.status === 401 ||
            (result as { errorCode?: string }).errorCode === 'UNAUTHENTICATED'
          ) {
            const errorMsg = 'Please sign in to save your dog profile.'
            setError(errorMsg)
            console.warn(
              '[useDogs] Unauthorized (401) - triggering onUnauthorized'
            )
            if (onUnauthorized) {
              onUnauthorized()
            }
            return { success: false, error: errorMsg }
          }

          // Handle 403 Dog Limit Reached - return error so component can show upgrade CTA
          if (
            result.status === 403 &&
            responseData?.code === 'DOG_LIMIT_REACHED'
          ) {
            const errorMsg = responseData?.message || 'Dog limit reached'
            setError(errorMsg)
            console.warn(
              '[useDogs] Dog limit reached (403) - returning error for upgrade CTA',
              {
                requiresUpgrade: responseData?.requiresUpgrade,
                currentUsage: responseData?.currentUsage,
                limit: responseData?.limit,
              }
            )
            return {
              success: false,
              error: errorMsg,
              requiresUpgrade: responseData?.requiresUpgrade,
              code: responseData?.code,
            }
          }

          // Server failed: persist to localStorage so data isn't lost, but return failure.
          // Play matching uses only DB-persisted dog data; local-only save must not be reported as success.
          console.error(
            '[useDogs] Server save failed, persisting to localStorage only (Play will not use until saved to server)',
            {
              status: result.status,
              json: responseData,
            }
          )

          const traitsJson = (dogData as any).traitsJson || null
          const newDogId =
            dogId || `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`
          const tempDog: Dog = {
            id: newDogId,
            userId: userId!,
            name: dogData.name,
            breed: dogData.breed,
            size: (dogData.size || 'medium') as Dog['size'],
            ageBracket: (dogData.ageBracket || 'adult') as Dog['ageBracket'],
            photoUrl: dogData.photoUrl || null,
            energyLevel: dogData.energyLevel || null,
            sociabilityDogs: dogData.sociabilityDogs || null,
            sociabilityPeople: dogData.sociabilityPeople || null,
            playStyles: dogData.playStyles || [],
            leashBehavior: dogData.leashBehavior || null,
            noiseSensitivity: dogData.noiseSensitivity || null,
            crowdTolerance: dogData.crowdTolerance || null,
            weatherTolerance: dogData.weatherTolerance || [],
            surfaceSensitivity: dogData.surfaceSensitivity || null,
            carComfort: dogData.carComfort || null,
            hasJointIssues: dogData.hasJointIssues || false,
            hasBreathingIssues: dogData.hasBreathingIssues || false,
            hasAllergies: dogData.hasAllergies || false,
            allergyTags: dogData.allergyTags || [],
            traitsJson: traitsJson,
            createdAt: new Date(),
            updatedAt: new Date(),
          }
          persistDogToLocal(userId!, tempDog)
          setLastSavedAt(new Date())

          const serverFailedMsg = responseData?.message || 'Server save failed'
          setError(serverFailedMsg)
          console.log(
            '[useDogs] Dog persisted to localStorage only:',
            tempDog.id
          )
          return {
            success: false,
            error:
              'Saved locally only. Play matching uses server-saved profiles—please try again or check your connection.',
            dog: tempDog,
            source: 'local',
          }
        }

        // Handle 2xx success - dog saved to database
        if (result.ok && responseData?.success) {
          const savedDog = responseData.dog

          console.log('[useDogs] ✅ Dog saved to database successfully:', {
            dogId: savedDog.id,
            dogName: savedDog.name,
            userId: userId,
            source: 'database',
          })

          // Update state and localStorage in a single functional update
          setDogs((prev) => {
            let updatedDogs: Dog[]

            if (dogId) {
              // Update existing dog - replace by id
              updatedDogs = prev.map((d) => (d.id === dogId ? savedDog : d))
              // Safety check: if dog wasn't found, add it (shouldn't happen but be safe)
              if (!updatedDogs.find((d) => d.id === dogId)) {
                updatedDogs = [...prev, savedDog]
              }
            } else {
              // New dog - check if it already exists (shouldn't, but be safe)
              const existingIndex = prev.findIndex((d) => d.id === savedDog.id)
              if (existingIndex >= 0) {
                // Replace existing
                updatedDogs = prev.map((d, idx) =>
                  idx === existingIndex ? savedDog : d
                )
              } else {
                // Add new
                updatedDogs = [...prev, savedDog]
              }
            }

            // Deduplicate by id (safety check)
            const uniqueDogs = updatedDogs.reduce((acc: Dog[], dog: Dog) => {
              if (!acc.find((d) => d.id === dog.id)) {
                acc.push(dog)
              }
              return acc
            }, [])

            // Save to localStorage with enhanced error handling
            if (typeof window !== 'undefined' && window.localStorage) {
              try {
                const serialized = JSON.stringify(uniqueDogs)
                localStorage.setItem(`wagamour_dogs_${userId}`, serialized)
                window.dispatchEvent(new CustomEvent('dogsUpdated'))
                console.log(
                  '[useDogs] ✓ Saved to localStorage after API success:',
                  {
                    totalDogs: uniqueDogs.length,
                    dataSize: `${(serialized.length / 1024).toFixed(2)} KB`,
                  }
                )
              } catch (e: any) {
                if (e.name === 'QuotaExceededError' || e.code === 22) {
                  console.error(
                    '[useDogs] ❌ localStorage quota exceeded after API save'
                  )
                } else {
                  console.warn(
                    '[useDogs] ⚠ Failed to save to localStorage after API success:',
                    e.name || e.message || e
                  )
                }
              }
            }

            return uniqueDogs
          })

          setLastSavedAt(new Date())
          setError(null)
          console.log('[useDogs] Dog saved successfully:', savedDog.id)
          if (typeof window !== 'undefined') {
            trackEvent('dog_profile_completed', {
              page_path: window.location.pathname,
              dog_id: savedDog.id,
              is_new: !dogId,
            })
          }
          return { success: true, dog: savedDog }
        }

        // This should never be reached, but TypeScript needs it
        return { success: false, error: 'Unexpected response format' }
      } catch (err: any) {
        // AbortError (e.g. request aborted) - do not surface to user
        if (err?.name === 'AbortError') {
          setSaving(false)
          return { success: false, error: undefined }
        }
        // Network error or fetch failure - fallback to localStorage
        console.error(
          '[useDogs] Save error (network/fetch), falling back to localStorage:',
          err
        )

        // Extract traitsJson from payload if present
        const traitsJson = (dogData as any).traitsJson || null

        // Generate stable ID for new dogs or use existing dogId
        const newDogId =
          dogId || `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`

        // Create a temporary dog object
        const tempDog: Dog = {
          id: newDogId,
          userId: userId!,
          name: dogData.name,
          breed: dogData.breed,
          size: (dogData.size || 'medium') as Dog['size'],
          ageBracket: (dogData.ageBracket || 'adult') as Dog['ageBracket'],
          photoUrl: dogData.photoUrl || null,
          energyLevel: dogData.energyLevel || null,
          sociabilityDogs: dogData.sociabilityDogs || null,
          sociabilityPeople: dogData.sociabilityPeople || null,
          playStyles: dogData.playStyles || [],
          leashBehavior: dogData.leashBehavior || null,
          noiseSensitivity: dogData.noiseSensitivity || null,
          crowdTolerance: dogData.crowdTolerance || null,
          weatherTolerance: dogData.weatherTolerance || [],
          surfaceSensitivity: dogData.surfaceSensitivity || null,
          carComfort: dogData.carComfort || null,
          hasJointIssues: dogData.hasJointIssues || false,
          hasBreathingIssues: dogData.hasBreathingIssues || false,
          hasAllergies: dogData.hasAllergies || false,
          allergyTags: dogData.allergyTags || [],
          traitsJson: traitsJson,
          createdAt: new Date(),
          updatedAt: new Date(),
        }

        // Persist to localStorage using helper
        persistDogToLocal(userId!, tempDog)

        setLastSavedAt(new Date())
        setError(null)
        console.log(
          '[useDogs] Dog saved to localStorage fallback (network error):',
          tempDog.id
        )
        return { success: true, dog: tempDog, source: 'local' }
      } finally {
        setSaving(false)
      }
    },
    [userId, onUnauthorized, persistDogToLocal]
  )

  // Delete dog
  const deleteDog = useCallback(
    async (dogId: string): Promise<{ success: boolean; error?: string }> => {
      if (!userId) {
        return { success: false, error: 'User ID is required' }
      }

      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`/api/dogs/${dogId}`, {
          method: 'DELETE',
        })

        const responseData = await response
          .json()
          .catch(() => ({ success: false }))

        if (response.ok && responseData.success) {
          // Update state and localStorage in a single functional update
          setDogs((prev) => {
            const updatedDogs = prev.filter((d) => d.id !== dogId)

            // Save to localStorage
            if (typeof window !== 'undefined') {
              try {
                localStorage.setItem(
                  `wagamour_dogs_${userId}`,
                  JSON.stringify(updatedDogs)
                )
                window.dispatchEvent(new CustomEvent('dogsUpdated'))
              } catch (e) {
                console.warn(
                  '[useDogs] Failed to save to localStorage after delete:',
                  e
                )
              }
            }

            return updatedDogs
          })

          return { success: true }
        } else {
          // Even if API fails, try to delete from localStorage as fallback
          if (
            responseData.code === 'DB_CONFIG_ERROR' ||
            responseData.code === 'DB_NOT_CONFIGURED' ||
            responseData.code === 'DB_ERROR'
          ) {
            console.warn(
              '[useDogs] Database not configured - deleting from localStorage as fallback'
            )
            setDogs((prev) => {
              const updatedDogs = prev.filter((d) => d.id !== dogId)

              if (typeof window !== 'undefined') {
                try {
                  localStorage.setItem(
                    `wagamour_dogs_${userId}`,
                    JSON.stringify(updatedDogs)
                  )
                  window.dispatchEvent(new CustomEvent('dogsUpdated'))
                } catch (e) {
                  console.warn(
                    '[useDogs] Failed to save to localStorage after delete fallback:',
                    e
                  )
                }
              }

              return updatedDogs
            })
            return { success: true }
          }

          const errorMsg = responseData.message || 'Failed to delete dog'
          setError(errorMsg)
          return { success: false, error: errorMsg }
        }
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          setLoading(false)
          return { success: false }
        }
        console.error('[useDogs] Delete error:', err)
        // On network error, still try to delete from localStorage
        setDogs((prev) => {
          const updatedDogs = prev.filter((d) => d.id !== dogId)

          if (typeof window !== 'undefined') {
            try {
              localStorage.setItem(
                `wagamour_dogs_${userId}`,
                JSON.stringify(updatedDogs)
              )
              window.dispatchEvent(new CustomEvent('dogsUpdated'))
            } catch (e) {
              console.warn(
                '[useDogs] Failed to save to localStorage after delete network error:',
                e
              )
            }
          }

          return updatedDogs
        })

        const errorMsg = 'Failed to delete dog'
        setError(errorMsg)
        return { success: false, error: errorMsg }
      } finally {
        setLoading(false)
      }
    },
    [userId]
  )

  // Load dogs when userId changes (but not when loadDogs function reference changes)
  useEffect(() => {
    if (userId) {
      loadDogs()
      return
    }
    setDogs([])
    setLoading(false)
    setLoaded(false)
    setError(null)
  }, [userId]) // Only depend on userId, not loadDogs to avoid unnecessary reloads

  return {
    dogs,
    loading,
    loaded,
    saving,
    error,
    lastSavedAt,
    loadDogs,
    saveDog,
    deleteDog,
    clearError: () => setError(null),
  }
}
